
import {
  AlertCircle,
  AlertTriangle,
  Brain,
  CheckCircle2,
  Loader2,
  Target,
  ThumbsUp,
} from "lucide-react";

import { mapCandidateDetails } from "@/modules/hrPortal/utils/candidateDetailsMapper";
import {
  getScoreBgColor,
  getScoreTextColor,
} from "@/modules/hrPortal/utils/scoreHelpers";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { Progress } from "@/shared/components/ui/progress";

type AIAnalysis = ReturnType<typeof mapCandidateDetails>["aiAnalysis"];

interface AIAnalysisCardProps {
  aiAnalysis: AIAnalysis;

  aiRun: {
    id: string;
    applicationId: string;
    status: string;
    provider: string | null;
    model: string | null;
    createdAt: string;
    startedAt: string | null;
    completedAt: string | null;
    errorMessage: string | null;
  } | null;

  onGenerateAIReport: () => Promise<boolean>;
  aiGenerating: boolean;
  canGenerateAIReport: boolean;
}

export default function AIAnalysisCard({
  aiAnalysis,
  aiRun,
  onGenerateAIReport,
  aiGenerating,
  canGenerateAIReport,
}: AIAnalysisCardProps) {
  const aiStatus = aiRun?.status ?? null;

  const isAIProcessing =
    aiGenerating ||
    aiStatus === "QUEUED" ||
    aiStatus === "PROCESSING" ||
    aiStatus === "RETRYING";

  return (
    <Card className="w-full min-w-0 overflow-hidden">
      {/* Header */}
      <CardHeader className="p-4 sm:p-5 lg:p-6">
        <div className="flex min-w-0 flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 flex-1">
            <CardTitle className="flex min-w-0 items-center gap-2 text-base sm:text-lg">
              <Brain className="h-5 w-5 shrink-0 text-blue-600" />
              <span className="break-words">AI Analysis Report</span>
            </CardTitle>

            <CardDescription className="mt-2 break-words text-sm leading-relaxed">
              Automated screening results based on resume and video analysis
            </CardDescription>
          </div>

          {canGenerateAIReport && (
            <Button
              type="button"
              onClick={() => {
                void onGenerateAIReport();
              }}
              disabled={isAIProcessing}
              className="w-full shrink-0 gap-2 sm:w-auto"
            >
              {isAIProcessing ? (
                <>
                  <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <Brain className="h-4 w-4 shrink-0" />
                  <span>Generate AI Report</span>
                </>
              )}
            </Button>
          )}
        </div>
      </CardHeader>

      <CardContent className="min-w-0 px-4 pb-4 sm:px-5 sm:pb-5 lg:px-6 lg:pb-6">
        {/* ATS Score and recommendation */}
        <div className="mb-5 flex min-w-0 flex-col gap-4 rounded-lg border border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 p-3 sm:mb-6 sm:flex-row sm:items-center sm:justify-between sm:p-4">
          <div className="flex min-w-0 items-center gap-3">
            <div
              className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-full sm:h-16 sm:w-16 ${getScoreBgColor(
                aiAnalysis.atsScore,
              )}`}
            >
              <span
                className={`text-lg font-bold sm:text-2xl ${getScoreTextColor(
                  aiAnalysis.atsScore,
                )}`}
              >
                {aiAnalysis.atsScore != null
                  ? `${aiAnalysis.atsScore}%`
                  : "N/A"}
              </span>
            </div>

            <div className="min-w-0">
              <h3 className="text-base font-semibold sm:text-lg">
                ATS Score
              </h3>
              <p className="text-xs text-muted-foreground sm:text-sm">
                Overall candidate match
              </p>
            </div>
          </div>

          <div className="flex min-w-0 flex-col items-start gap-1 sm:items-end sm:text-right">
            <Badge className="max-w-full whitespace-normal break-words bg-green-600">
              <ThumbsUp className="mr-1 h-3 w-3 shrink-0" />
              <span>{aiAnalysis.recommendation}</span>
            </Badge>

            <p className="text-xs text-muted-foreground sm:text-sm">
              {aiAnalysis.confidence != null
                ? `${aiAnalysis.confidence}%`
                : "N/A"}{" "}
              confidence
            </p>
          </div>
        </div>

        {/* Score breakdown */}
        <div className="mb-5 grid min-w-0 grid-cols-1 gap-3 sm:mb-6 sm:grid-cols-2 lg:grid-cols-3 sm:gap-4">
          {[
            { label: "Resume Score", value: aiAnalysis.resumeScore },
            { label: "Video Score", value: aiAnalysis.videoScore },
            { label: "JD Match", value: aiAnalysis.jdMatchScore },
          ].map((score) => (
            <div
              key={score.label}
              className="min-w-0 rounded-lg bg-slate-50 p-3 sm:p-4"
            >
              <div className="mb-2 flex min-w-0 items-center justify-between gap-2">
                <span className="min-w-0 break-words text-sm text-muted-foreground">
                  {score.label}
                </span>

                <span
                  className={`shrink-0 font-bold ${getScoreTextColor(
                    score.value,
                  )}`}
                >
                  {score.value != null ? `${score.value}%` : "N/A"}
                </span>
              </div>

              <Progress
                value={score.value ?? 0}
                className="h-2 w-full"
              />
            </div>
          ))}
        </div>

        {/* Strengths and concerns */}
        <div className="mb-5 grid min-w-0 grid-cols-1 gap-5 sm:mb-6 sm:grid-cols-2 sm:gap-4 lg:gap-6">
          <div className="min-w-0">
            <h4 className="mb-3 flex items-center gap-2 font-semibold">
              <ThumbsUp className="h-4 w-4 shrink-0 text-green-600" />
              Strengths
            </h4>

            {aiAnalysis.strengths.length > 0 ? (
              <ul className="space-y-2">
                {aiAnalysis.strengths.map((strength, index) => (
                  <li
                    key={index}
                    className="flex min-w-0 items-start gap-2 text-sm"
                  >
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />

                    <span className="min-w-0 break-words text-muted-foreground">
                      {strength}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">
                No strengths available.
              </p>
            )}
          </div>

          <div className="min-w-0">
            <h4 className="mb-3 flex items-center gap-2 font-semibold">
              <AlertTriangle className="h-4 w-4 shrink-0 text-yellow-500" />
              Areas of Concern
            </h4>

            {aiAnalysis.weaknesses.length > 0 ? (
              <ul className="space-y-2">
                {aiAnalysis.weaknesses.map((weakness, index) => (
                  <li
                    key={index}
                    className="flex min-w-0 items-start gap-2 text-sm"
                  >
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-yellow-500" />

                    <span className="min-w-0 break-words text-muted-foreground">
                      {weakness}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">
                No areas of concern available.
              </p>
            )}
          </div>
        </div>

        {/* Skill assessment */}
        <div className="min-w-0">
          <h4 className="mb-3 flex items-center gap-2 font-semibold">
            <Target className="h-4 w-4 shrink-0 text-blue-600" />
            Skill Assessment
          </h4>

          {aiAnalysis.skillGaps.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {aiAnalysis.skillGaps.map((skill) => (
                <Badge
                  key={skill.skill}
                  variant={skill.hasExperience ? "default" : "secondary"}
                  className={`max-w-full whitespace-normal break-words ${
                    skill.hasExperience
                      ? "bg-green-600"
                      : "bg-yellow-100 text-yellow-700"
                  }`}
                >
                  {skill.hasExperience ? (
                    <CheckCircle2 className="mr-1 h-3 w-3 shrink-0" />
                  ) : (
                    <AlertCircle className="mr-1 h-3 w-3 shrink-0" />
                  )}

                  <span>{skill.skill}</span>
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              No skill assessment available.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
